/**
 * TodayDashboardPanel
 *
 * Admin → "Today" tab. Single-glance view of what the team has done so
 * far today: quotations created, walk-ins logged, follow-ups due
 * tomorrow, per-salesperson leaderboard. Auto-refreshes every 60s so
 * the admin doesn't have to reload the page to see live activity.
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../ui/button';
import {
  RefreshCw, FileText, Users, MessageCircle, AlertTriangle, Loader2,
  CalendarClock, TrendingUp, Trophy, ChevronLeft, ChevronRight, CalendarDays,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { todayIst } from '../../lib/store';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatINR = (n) => {
  const num = Number(n || 0);
  if (num >= 10000000) return `₹${(num / 10000000).toFixed(2)}Cr`;
  if (num >= 100000) return `₹${(num / 100000).toFixed(2)}L`;
  if (num >= 1000) return `₹${(num / 1000).toFixed(1)}K`;
  return `₹${Math.round(num)}`;
};

function DeltaChip({ today, yesterday, valueFormatter, suffix = '', compareLabel = 'yesterday' }) {
  // Compare current period's number to the same window in the comparison period.
  // Renders a coloured chip showing absolute and % delta.
  const t = Number(today || 0);
  const y = Number(yesterday || 0);
  const diff = t - y;
  if (y === 0 && t === 0) return null;
  const shortLabel = compareLabel === 'yesterday' ? 'yest.' : 'prev. day';
  if (diff === 0) {
    return (
      <span className="inline-flex items-center text-[10.5px] font-semibold text-heda-mute bg-heda-cream rounded-full px-2 py-0.5 mt-2">
        = vs {shortLabel}
      </span>
    );
  }
  const pct = y === 0 ? null : Math.round((diff / Math.max(1, y)) * 100);
  const isUp = diff > 0;
  const colour = isUp ? 'text-emerald-700 bg-emerald-50' : 'text-red-700 bg-red-50';
  const arrow = isUp ? '▲' : '▼';
  const absDisp = valueFormatter ? valueFormatter(Math.abs(diff)) : Math.abs(diff);
  return (
    <span className={`inline-flex items-center gap-1 text-[10.5px] font-semibold rounded-full px-2 py-0.5 mt-2 ${colour}`}>
      {arrow} {absDisp}{suffix}{pct !== null ? ` (${isUp ? '+' : ''}${pct}%)` : ''} vs {shortLabel}
    </span>
  );
}

function StatTile({ icon, label, value, hint, accent, testId, delta }) {
  const Icon = icon;
  return (
    <div
      className={`rounded-2xl border bg-white p-5 ${accent || 'border-heda-line'}`}
      data-testid={testId}
    >
      <div className="flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-wider text-heda-mute">{label}</span>
        <span className="flex h-8 w-8 items-center justify-center rounded-full border border-heda-gold/30 bg-heda-gold/10 text-heda-gold">
          <Icon className="w-4 h-4" />
        </span>
      </div>
      <div className="mt-2 font-display text-3xl font-bold text-heda-ink tabular-nums">{value}</div>
      {hint && <p className="text-[11.5px] text-heda-mute mt-0.5">{hint}</p>}
      {delta}
    </div>
  );
}

// Compute today's IST date as YYYY-MM-DD (used as the default selection
// and as the upper bound on the date picker). iter-119b: switched to
// the shared `todayIst` helper — the old inline math added
// `getTimezoneOffset() + 5.5h` which double-counted on IST devices and
// silently returned the UTC date instead of the IST one.
const getTodayISTDateString = todayIst;

function shiftDate(yyyyMmDd, deltaDays) {
  const [y, m, d] = yyyyMmDd.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + deltaDays);
  const ny = dt.getUTCFullYear();
  const nm = String(dt.getUTCMonth() + 1).padStart(2, '0');
  const nd = String(dt.getUTCDate()).padStart(2, '0');
  return `${ny}-${nm}-${nd}`;
}

function prettyDate(yyyyMmDd) {
  const [y, m, d] = yyyyMmDd.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.toLocaleDateString('en-IN', {
    weekday: 'short', day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC',
  });
}

export default function TodayDashboardPanel() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const todayStr = useMemo(() => getTodayISTDateString(), []);
  // Seed the picker from `?date=YYYY-MM-DD` (used by the
  // QuotationDetail Back button so the user lands on the same day they
  // clicked through from). Falls back to today when absent or malformed.
  const initialDate = useMemo(() => {
    const raw = (searchParams.get('date') || '').trim();
    return /^\d{4}-\d{2}-\d{2}$/.test(raw) && raw <= todayStr ? raw : todayStr;
  }, [searchParams, todayStr]);
  const [selectedDate, setSelectedDate] = useState(initialDate);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lastFetch, setLastFetch] = useState(null);

  const isToday = selectedDate === todayStr;

  const fetchStats = useCallback(async (dateOverride) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('heda_token');
      const d = dateOverride || selectedDate;
      const res = await axios.get(`${API_BASE}/admin/today-stats`, {
        params: { date: d },
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setData(res.data);
      setLastFetch(new Date());
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load stats');
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  // Initial load whenever the selected date changes.
  // We schedule via queueMicrotask so the state-setter calls inside
  // fetchStats happen *after* the effect returns — required to satisfy
  // the new `react-hooks/set-state-in-effect` rule.
  useEffect(() => {
    let cancelled = false;
    queueMicrotask(() => { if (!cancelled) fetchStats(); });
    return () => { cancelled = true; };
  }, [fetchStats]);

  // Auto-refresh every 60s ONLY when viewing today. For past days the
  // numbers are frozen so polling is pointless.
  useEffect(() => {
    if (!isToday) {
      return undefined;
    }
    const id = setInterval(() => { fetchStats(); }, 60000);
    return () => clearInterval(id);
  }, [fetchStats, isToday]);

  if (!data && loading) {
    return <p className="flex items-center justify-center gap-2 text-sm text-heda-mute py-12"><Loader2 className="w-5 h-5 animate-spin text-heda-gold" /> Loading stats…</p>;
  }
  if (!data) {
    return <p className="text-sm text-heda-mute py-12 text-center">No data yet.</p>;
  }

  const q = data.quotations;
  const w = data.walkins;
  const y = q.yesterday || { total_count: 0, total_value: 0, by_status: { closed: 0, pending: 0, lost: 0 } };
  const closedRatio = q.total_count > 0 ? Math.round((q.by_status.closed / q.total_count) * 100) : 0;
  const compareLabel = data.compare_label || (isToday ? 'yesterday' : 'previous day');

  return (
    <div className="space-y-5" data-testid="admin-today-panel">
      {/* Header + Date Navigator */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="min-w-0">
          <h2 className="font-display text-[17px] sm:text-[19px] font-bold text-heda-ink flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-heda-gold" />
            {isToday ? "Today's Live Dashboard" : `Dashboard — ${prettyDate(selectedDate)}`}
          </h2>
          <p className="text-[11.5px] text-heda-mute mt-0.5">
            <span className="font-semibold text-heda-ink">{data.date_ist}</span>
            {' · '}Last updated: {lastFetch ? lastFetch.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '—'}
            {isToday ? ' · Auto-refresh every 60s' : ' · Historical view (no auto-refresh)'}
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap" data-testid="today-date-controls">
          <Button
            variant="outline"
            size="sm"
            className="h-10 w-10 p-0 rounded-full border-heda-line bg-white text-heda-ink shadow-none hover:bg-white hover:border-heda-gold hover:text-heda-gold"
            onClick={() => setSelectedDate((d) => shiftDate(d, -1))}
            disabled={loading}
            data-testid="today-prev-day-btn"
            title="Previous day"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div className="relative">
            <CalendarDays className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-heda-gold pointer-events-none" />
            <input
              type="date"
              value={selectedDate}
              max={todayStr}
              onChange={(e) => {
                const v = e.target.value;
                if (!v) return;
                if (v > todayStr) {
                  toast.error('Cannot view a future date');
                  return;
                }
                setSelectedDate(v);
              }}
              className="h-10 pl-8 pr-3 text-base sm:text-sm border border-heda-line rounded-full bg-white text-heda-ink focus:outline-none focus:border-heda-gold"
              data-testid="today-date-picker"
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            className="h-10 w-10 p-0 rounded-full border-heda-line bg-white text-heda-ink shadow-none hover:bg-white hover:border-heda-gold hover:text-heda-gold"
            onClick={() => setSelectedDate((d) => (d === todayStr ? d : shiftDate(d, 1)))}
            disabled={loading || isToday}
            data-testid="today-next-day-btn"
            title="Next day"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
          {!isToday && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedDate(todayStr)}
              disabled={loading}
              data-testid="today-jump-today-btn"
              className="h-10 rounded-full text-heda-gold hover:bg-heda-cream hover:text-heda-gold"
            >
              Jump to today
            </Button>
          )}
          <Button className="h-10 rounded-full border-heda-line bg-white text-[12.5px] text-heda-ink shadow-none hover:bg-white hover:border-heda-gold hover:text-heda-gold" onClick={() => fetchStats()} variant="outline" size="sm" disabled={loading} data-testid="today-refresh-btn">
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* KPI tiles — value-first Quotations, dropped the
          Closed/Pending/Lost tile (the new Quotations-created list +
          the Top Salespeople table already carry that info), grid
          collapsed from 4 → 3 columns. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <StatTile
          icon={FileText}
          label={isToday ? 'Quotations Today' : 'Quotations'}
          value={formatINR(q.total_value)}
          hint={q.total_count > 0
            ? `${q.total_count} quotation${q.total_count === 1 ? '' : 's'} · ${closedRatio}% closed`
            : 'No quotations'}
          testId="today-tile-quotations"
          delta={<DeltaChip today={q.total_value} yesterday={y.total_value} valueFormatter={formatINR} compareLabel={compareLabel} />}
        />
        <StatTile
          icon={Users}
          label={isToday ? 'Walk-ins Logged' : 'Walk-ins'}
          value={w.today_count}
          hint={w.pending_review > 0 ? `⚠ ${w.pending_review} need review` : 'All clean'}
          accent={w.pending_review > 0 ? 'border-amber-200' : 'border-heda-line'}
          testId="today-tile-walkins"
          delta={<DeltaChip today={w.today_count} yesterday={w.yesterday_count} compareLabel={compareLabel} />}
        />
        <StatTile
          icon={CalendarClock}
          label={isToday ? 'Follow-ups Due Tomorrow' : 'Follow-ups Due Next Day'}
          value={w.follow_ups_tomorrow}
          hint={w.follow_ups_tomorrow > 0 ? "Don't miss these" : 'Schedule is clear'}
          testId="today-tile-followups"
        />
      </div>

      {/* Pending review banner */}
      {w.pending_review > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
          <div className="text-[13px] text-amber-700">
            <span className="font-semibold">{w.pending_review} walk-in{w.pending_review > 1 ? 's' : ''}</span>
            {' '}couldn&apos;t be auto-transcribed. Open the <span className="font-semibold">Walk-ins tab</span> to listen and complete them.
          </div>
        </div>
      )}

      {/* Quotations created on the selected day — placed where
          the old Closed/Pending/Lost tile used to sit. Newest first;
          respects the panel's date picker (past-day view lists that day's
          quotes). Click a row to open the quotation. */}
      <div className="heda-card overflow-hidden" data-testid="today-quotations-list-card">
          <div className="flex items-center justify-between border-b border-heda-line px-5 py-3">
          <h3 className="font-display text-[15px] font-bold text-heda-ink flex items-center gap-2">
            <FileText className="w-4 h-4 text-heda-gold" />
            {isToday ? 'Quotations Created Today' : `Quotations Created — ${prettyDate(selectedDate)}`}
            <span className="text-[11.5px] font-normal text-heda-mute">
              ({(q.list || []).length})
            </span>
          </h3>
          </div>
          {(q.list || []).length === 0 ? (
            <p className="text-sm text-heda-mute px-5 py-10 text-center" data-testid="today-quotations-list-empty">
              {isToday ? 'No quotations created yet today.' : 'No quotations were created on this day.'}
            </p>
          ) : (
            <div className="overflow-x-auto max-h-96 overflow-y-auto">
              <table className="w-full text-left text-sm">
                <thead className="heda-thead sticky top-0">
                  <tr>
                    <th className="px-3 sm:px-4 py-2.5 font-semibold whitespace-nowrap hidden sm:table-cell">Quote #</th>
                    <th className="px-3 sm:px-4 py-2.5 font-semibold">Customer</th>
                    <th className="px-3 sm:px-4 py-2.5 font-semibold hidden md:table-cell">Salesperson</th>
                    <th className="px-3 sm:px-4 py-2.5 font-semibold text-right">Value</th>
                    <th className="px-3 sm:px-4 py-2.5 font-semibold text-center">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(q.list || []).map((row) => {
                    const st = (row.status || 'pending').toLowerCase();
                    const badge = st === 'closed'
                      ? { cls: 'bg-emerald-50 text-emerald-700', label: '✓ Closed' }
                      : st === 'lost'
                        ? { cls: 'bg-red-50 text-red-700', label: '✕ Lost' }
                        : { cls: 'bg-amber-50 text-amber-700', label: '● Pending' };
                    return (
                      <tr
                        key={row.id}
                        className="border-t border-heda-line hover:bg-heda-cream/50 cursor-pointer"
                        onClick={() => navigate(`/quotations/${row.id}`)}
                        data-testid={`today-quotation-row-${row.id}`}
                      >
                        <td className="px-3 sm:px-4 py-3 font-mono text-xs text-heda-ink whitespace-nowrap hidden sm:table-cell">
                          {row.quotation_number || '—'}
                        </td>
                        <td className="px-3 sm:px-4 py-3 max-w-[180px]" title={row.customer_name}>
                          <div className="truncate font-medium text-heda-ink">{row.customer_name || '—'}</div>
                          <div className="truncate font-mono text-[11px] text-heda-mute sm:hidden">{row.quotation_number || '—'}</div>
                        </td>
                        <td className="px-3 sm:px-4 py-3 hidden md:table-cell">
                          <span>{row.created_by_name || '—'}</span>
                          {row.created_by_code && (
                            <span className="ml-1.5 rounded-full bg-heda-olive-tint px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-heda-olive-dark">{row.created_by_code}</span>
                          )}
                        </td>
                        <td className="px-3 sm:px-4 py-3 text-right tabular-nums font-bold text-heda-ink whitespace-nowrap">
                          {formatINR(row.grand_total)}
                        </td>
                        <td className="px-3 sm:px-4 py-3 text-center">
                          <span className={`inline-flex items-center whitespace-nowrap px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${badge.cls}`}>
                            {badge.label}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
      </div>

      {/* Leaderboard */}
      <div className="heda-card overflow-hidden">
          <div className="border-b border-heda-line px-5 py-3">
          <h3 className="font-display text-[15px] font-bold text-heda-ink flex items-center gap-2">
            <Trophy className="w-4 h-4 text-heda-gold" /> {isToday ? 'Top Salespeople Today' : `Top Salespeople — ${prettyDate(selectedDate)}`}
          </h3>
          </div>
          {q.top_salespeople.length === 0 ? (
            <p className="text-sm text-heda-mute px-5 py-10 text-center">
              {isToday
                ? 'No quotations yet today — leaderboard updates live.'
                : 'No quotations on this day.'}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="heda-thead">
                  <tr>
                    <th className="px-3 sm:px-4 py-2.5 font-semibold w-8 hidden sm:table-cell">#</th>
                    <th className="px-3 sm:px-4 py-2.5 font-semibold">Salesperson</th>
                    <th className="px-3 sm:px-4 py-2.5 font-semibold text-right">Quotes</th>
                    <th className="px-3 sm:px-4 py-2.5 font-semibold text-right">Value</th>
                    <th className="px-3 sm:px-4 py-2.5 font-semibold text-center whitespace-nowrap hidden md:table-cell">Closed / Pending / Lost</th>
                  </tr>
                </thead>
                <tbody>
                  {q.top_salespeople.map((u, i) => (
                    <tr key={u.user_id} className="border-t border-heda-line" data-testid={`today-leader-${u.user_id}`}>
                      <td className="px-3 sm:px-4 py-3 tabular-nums text-heda-mute hidden sm:table-cell">{i + 1}</td>
                      <td className="px-3 sm:px-4 py-3">
                        <span className="font-medium text-heda-ink">{u.user_name}</span>
                        {u.staff_code && <span className="ml-2 rounded-full bg-heda-olive-tint px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-heda-olive-dark">{u.staff_code}</span>}
                      </td>
                      <td className="px-3 sm:px-4 py-3 text-right tabular-nums">{u.count}</td>
                      <td className="px-3 sm:px-4 py-3 text-right tabular-nums font-bold text-heda-ink">{formatINR(u.value)}</td>
                      <td className="px-3 sm:px-4 py-3 text-center tabular-nums hidden md:table-cell">{u.closed} / {u.pending} / {u.lost}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </div>

      <p className="text-[11px] text-heda-mute flex items-center gap-1 justify-center pt-2">
        <MessageCircle className="w-3 h-3" /> A full digest is sent to your configured WhatsApp number every evening at 9:00 PM IST.
      </p>
    </div>
  );
}
